/**
 * Shared RoomNexa SaaS checkout helpers for website payment + landing subscription.
 * Keep package/addon/coupon payload shapes aligned with /api/v1/payments/* APIs.
 */

export const EMPTY_COUPON_DETAILS = Object.freeze({
  code: '',
  token: '',
  contactBound: false,
  discountPercent: 0,
  discountAmountInPaise: 0,
  finalAmountPreviewInPaise: 0,
  taxableAmountInPaise: 0,
  gstAmountInPaise: 0,
  totalAmountInPaise: 0,
});

export function createEmptyCouponDetails() {
  return { ...EMPTY_COUPON_DETAILS };
}

/** UI billing keys (monthly|quarterly|yearly) → payments API (monthly|quarterly|annual). */
export function toApiBillingInterval(billingInterval) {
  const normalized = String(billingInterval || '').trim().toLowerCase();
  if (normalized === 'yearly' || normalized === 'annual' || normalized === 'annually') {
    return 'annual';
  }
  if (normalized === 'quarterly') return 'quarterly';
  return 'monthly';
}

export function buildAddonsPayload(selectedAddonCodes = []) {
  return (Array.isArray(selectedAddonCodes) ? selectedAddonCodes : []).reduce((payload, code) => {
    const key = String(code || '').trim();
    if (!key) return payload;
    return { ...payload, [key]: true };
  }, {});
}

export function buildCouponContextSignature({
  packageId,
  billingInterval,
  rooms,
  region,
  addonCodes = [],
  subscriptionAddonOnly = false,
}) {
  return JSON.stringify({
    packageId: String(packageId || '').trim(),
    billing: toApiBillingInterval(billingInterval),
    rooms: Math.max(1, Number(rooms) || 1),
    region: String(region || 'IN').trim().toUpperCase() || 'IN',
    addons: buildAddonsPayload(addonCodes),
    subscriptionAddonOnly: Boolean(subscriptionAddonOnly),
  });
}

export function normalizeCouponCode(value) {
  return String(value || '').trim().toUpperCase();
}

export function paiseToMajor(paise) {
  const value = Number(paise);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, value) / 100;
}

export function normalizeCouponValidateResult(result, fallbackCode = '') {
  const code = normalizeCouponCode(result?.couponCode || fallbackCode);
  return {
    code,
    token: String(result?.couponValidationToken || '').trim(),
    contactBound: Boolean(result?.contactBound),
    discountPercent: Math.max(0, Math.min(100, Number(result?.discountPercent) || 0)),
    discountAmountInPaise: Number(result?.discountAmountInPaise) || 0,
    finalAmountPreviewInPaise: Number(result?.finalAmountPreviewInPaise) || 0,
    taxableAmountInPaise: Number(result?.breakdown?.taxableAmountInPaise) || 0,
    gstAmountInPaise: Number(result?.breakdown?.gstAmountInPaise) || 0,
    totalAmountInPaise:
      Number(result?.breakdown?.totalAmountInPaise)
      || Number(result?.finalAmountPreviewInPaise)
      || 0,
  };
}

export function buildOrderCouponFields({ couponApplied, couponDetails }) {
  if (!couponApplied || !couponDetails?.code) {
    return {
      couponCode: '',
      couponValidationToken: '',
    };
  }
  return {
    couponCode: normalizeCouponCode(couponDetails.code),
    couponValidationToken:
      couponDetails.contactBound && couponDetails.token
        ? String(couponDetails.token).trim()
        : '',
  };
}

export function buildCheckoutQuoteRequest({
  packageId,
  billingInterval,
  rooms,
  region,
  paymentCurrency,
  addonCodes = [],
  couponCode = '',
  customerEmail = '',
  customerPhone = '',
  subscriptionAddonOnly = false,
}) {
  const normalizedCoupon = normalizeCouponCode(couponCode);
  return {
    packageId: String(packageId || '').trim(),
    billing: toApiBillingInterval(billingInterval),
    rooms: Math.max(1, Number(rooms) || 1),
    region: String(region || 'IN').trim().toUpperCase() || 'IN',
    paymentCurrency: String(paymentCurrency || 'INR').trim().toUpperCase() || 'INR',
    subscriptionAddonOnly: Boolean(subscriptionAddonOnly),
    addons: buildAddonsPayload(addonCodes),
    couponCode: normalizedCoupon,
    customerEmail: normalizedCoupon ? String(customerEmail || '').trim().toLowerCase() : '',
    customerPhone: normalizedCoupon ? String(customerPhone || '').trim() : '',
  };
}

export function buildCouponValidateRequest({
  packageId,
  billingInterval,
  rooms,
  region,
  addonCodes = [],
  couponCode,
  customerEmail,
  customerPhone,
  subscriptionAddonOnly = false,
}) {
  return {
    packageId: String(packageId || '').trim(),
    billing: toApiBillingInterval(billingInterval),
    rooms: Math.max(1, Number(rooms) || 1),
    region: String(region || 'IN').trim().toUpperCase() || 'IN',
    subscriptionAddonOnly: Boolean(subscriptionAddonOnly),
    addons: buildAddonsPayload(addonCodes),
    couponCode: normalizeCouponCode(couponCode),
    customerEmail: String(customerEmail || '').trim().toLowerCase(),
    customerPhone: String(customerPhone || '').trim(),
  };
}

/** Prefer authoritative quote total; fall back to catalog estimate / coupon preview. */
export function resolveCheckoutDisplayTotal({
  catalogEstimateMajor = 0,
  checkoutQuote = null,
  couponApplied = false,
  couponDetails = null,
}) {
  const quoteTotal = Number(checkoutQuote?.display?.totalMajor);
  if (Number.isFinite(quoteTotal) && quoteTotal >= 0) {
    return quoteTotal;
  }
  if (couponApplied && couponDetails?.finalAmountPreviewInPaise > 0) {
    return paiseToMajor(couponDetails.finalAmountPreviewInPaise);
  }
  return Math.max(0, Number(catalogEstimateMajor) || 0);
}

export function resolveCheckoutSavingsMajor({
  catalogEstimateMajor = 0,
  checkoutQuote = null,
  couponApplied = false,
  couponDetails = null,
}) {
  if (!couponApplied) return 0;
  const total = resolveCheckoutDisplayTotal({
    catalogEstimateMajor,
    checkoutQuote,
    couponApplied,
    couponDetails,
  });
  const preCouponQuote = Number(checkoutQuote?.display?.taxableMajor) + Number(checkoutQuote?.display?.gstMajor);
  if (checkoutQuote?.couponApplied && Number.isFinite(preCouponQuote) && preCouponQuote > total) {
    return Number((preCouponQuote - total).toFixed(2));
  }
  if (couponDetails?.discountAmountInPaise > 0) {
    return paiseToMajor(couponDetails.discountAmountInPaise);
  }
  const estimate = Math.max(0, Number(catalogEstimateMajor) || 0);
  return estimate > total ? Number((estimate - total).toFixed(2)) : 0;
}
